import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { AtualizacaoValoresResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosPASEP = () => {
  const [valorOriginal, setValorOriginal] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [resultado, setResultado] = useState<AtualizacaoValoresResult | null>(null);
  
  const { calcularAtualizacaoValores, loading } = useCalculos();

  useCalculoPrefill("pasep", (v) => {
    setValorOriginal(v.valorOriginal ?? "");
    setDataInicial(v.dataInicial ?? "");
    setDataFinal(v.dataFinal ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!valorOriginal || !dataInicial || !dataFinal) {
      return;
    }

    try {
      const result = await calcularAtualizacaoValores({
        valorOriginal: parseFloat(valorOriginal),
        dataInicial,
        dataFinal,
        indice: 'ipca',
        taxaJuros: 0
      });
      setResultado(result);
      addToHistory({
        type: "pasep",
        label: "Correção PASEP",
        values: { valorOriginal, dataInicial, dataFinal },
        summary: `Original ${formatCurrency(parseFloat(valorOriginal))} • Diferença ${formatCurrency(result.correcaoMonetaria)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Correção PASEP</CardTitle>
        <p className="text-sm text-muted-foreground">
          Diferença de correção aplicando IPCA-E ao invés de TR
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor-original">Valor Original PASEP</Label>
            <Input
              id="valor-original"
              type="number"
              placeholder="1000.00"
              value={valorOriginal}
              onChange={(e) => setValorOriginal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-inicial">Data Inicial</Label>
            <Input
              id="data-inicial"
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-final">Data Final</Label>
            <Input
              id="data-final"
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Correção PASEP"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Resultado da Correção:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Valor Original:</strong> {formatCurrency(parseFloat(valorOriginal))}</p>
                <p><strong>Valor Corrigido (IPCA-E):</strong> {formatCurrency(resultado.valorAtualizado)}</p>
              </div>
              <div>
                <p className="text-green-600"><strong>Diferença a Receber:</strong> {formatCurrency(resultado.correcaoMonetaria)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              * Diferença entre correção pelo IPCA-E vs TR aplicada
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};